/* Focus management for theme-owned modal dialogs. Vendor dialogs retain their own lifecycle. */
const themeDialogInertOwners = new WeakMap();

class ThemeDialogFocus {
  constructor(dialog, onClose) {
    this.dialog = dialog;
    this.onClose = onClose;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onFocus = this.onFocus.bind(this);
  }

  focusable() {
    return Array.from(this.dialog.querySelectorAll('a[href], button, input, select, textarea, summary, iframe, [tabindex]'))
        .filter((element) => !element.disabled && element.tabIndex >= 0 && !element.closest('[inert]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  }

  activate() {
    if (this.active) return;
    this.active = true;
    this.previousFocus = document.activeElement;
    this.background = new Set();
    this.isolateBackground();
    this.observer = new MutationObserver(() => this.isolateBackground());
    this.observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('focusin', this.onFocus);
    (this.focusable()[0] || this.dialog).focus({ preventScroll: true });
  }

  isolateBackground() {
    // Include vendor nodes inserted while open, without inerting our own ancestor.
    let branch = this.dialog;
    while (branch.parentElement && branch !== document.body) {
      Array.from(branch.parentElement.children).forEach((sibling) => {
        if (sibling === branch || this.background.has(sibling) || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;

        const owners = themeDialogInertOwners.get(sibling);
        if (owners) {
          themeDialogInertOwners.set(sibling, owners + 1);
          this.background.add(sibling);
        } else if (!sibling.inert) {
          sibling.inert = true;
          themeDialogInertOwners.set(sibling, 1);
          this.background.add(sibling);
        }
      });
      branch = branch.parentElement;
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.onClose();
    } else if (event.key === 'Tab') {
      const elements = this.focusable();
      const first = elements[0] || this.dialog;
      const last = elements[elements.length - 1] || this.dialog;
      if (!elements.length || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last) || !this.dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }
  }

  onFocus(event) {
    if (!this.dialog.contains(event.target)) (this.focusable()[0] || this.dialog).focus({ preventScroll: true });
  }

  deactivate() {
    if (!this.active) return;
    this.active = false;
    this.observer?.disconnect();
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('focusin', this.onFocus);
    this.background.forEach((element) => {
      const owners = themeDialogInertOwners.get(element);
      if (owners <= 1) {
        themeDialogInertOwners.delete(element);
        element.inert = false;
      } else {
        themeDialogInertOwners.set(element, owners - 1);
      }
    });
    this.background.clear();
    const target = this.previousFocus;
    if (target && target !== document.body && target.isConnected && !target.closest('[inert]') && target.getClientRects().length) {
      target.focus({ preventScroll: true });
    } else {
      document.getElementById('MainContent')?.focus({ preventScroll: true });
    }
  }
}

document.addEventListener('click', (event) => {
  const skip = event.target.closest('a[href="#ProductGridContainer"]');
  if (skip) document.getElementById('ProductGridContainer')?.focus();

  const opener = event.target.closest('[data-size-chart-open]');
  const modal = document.getElementById('promo-modal');
  if (!modal) return;
  if (opener) {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');
    if (!modal.themeFocus) {
      modal.themeFocus = new ThemeDialogFocus(modal, () => {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');
        modal.themeFocus.deactivate();
      });
    }
    modal.themeFocus.activate();
  } else if (event.target.closest('[data-modal-close]') && modal.classList.contains('is-open')) {
    modal.themeFocus?.onClose();
  }
});

// Empty CMS headings carry no information. Preserve their original box and margins.
document.querySelectorAll('.article-template__content :is(h1,h2,h3,h4,h5,h6)').forEach((heading) => {
  if (heading.textContent.trim() || heading.querySelector('img,svg,video,iframe,input,button') || heading.getAttribute('aria-label')) return;
  const style = getComputedStyle(heading);
  const spacer = document.createElement('div');
  spacer.className = heading.className;
  spacer.setAttribute('aria-hidden', 'true');
  spacer.style.cssText = heading.style.cssText;
  ['font', 'margin', 'padding', 'border', 'minHeight', 'lineHeight', 'letterSpacing'].forEach((property) => { spacer.style[property] = style[property]; });
  spacer.innerHTML = heading.innerHTML;
  heading.replaceWith(spacer);
});
